import useMessagingStatusFilters from "@/composables/v2.1/useMessagingStatusFilters";
import axios from "@/libs/axios";
// @ts-ignore
import router from "@/router";
import { useChannelsStore } from "@/store/useChannelsStore";
import { useContactsStore } from "@/store/useContactsStore";
import { isChatAssignedToMeV21, resolveRemainingCustomerCareWindow, shouldAssignV21 } from "@/utils/chats";
import { ActiveChat, UpdateChatsListChat } from "@/views/modules/messaging-inbox/useChat";
import { RequestQueryBuilder } from "@rewiko/crud-request";
import {
    Asset,
    AssetDto,
    Assignee,
    Attachment,
    AttachmentEntryDto,
    ChatsListChat,
    ChatStatus,
    Contact,
    EntryDtoType,
    SendMessage,
    SendResponse,
    TextEntryDto,
} from "@saysimple-platform/dtos";
import { uniqueId } from "lodash";
import { DateTime } from "luxon";
import { defineStore } from "pinia";

interface State {
    chatsList: ChatsListChat[];
    activeChat: ActiveChat;
    isUpdatingChatId?: number;
    isLoadingChats: boolean;
    isLoadingChat: boolean;
    isLoadingMoreEntries: boolean;
    chatsListPagination: {
        count: number;
        total: number;
        page: number;
        pageCount: number;
        perPage: number;
    };
    chatLogPagination: {
        count: number;
        total: number;
        page: number;
        pageCount: number;
        perPage: number;
    };
    req: RequestQueryBuilder;
}

const endpoint = `${process.env.VUE_APP_PUBLIC_API}/messaging-chats`;
const PER_PAGE = 50;

export const useChatsStore = defineStore("messagingInbox", {
    state: (): State => ({
        chatsList: [],
        activeChat: null,
        isUpdatingChatId: null,
        isLoadingChats: true, // @note default to true.
        isLoadingChat: false,
        isLoadingMoreEntries: false,
        chatsListPagination: {
            count: 0,
            total: 0,
            page: 1,
            pageCount: 0,
            perPage: PER_PAGE,
        },
        chatLogPagination: {
            count: 0,
            total: 0,
            page: 1,
            pageCount: 0,
            perPage: PER_PAGE,
        },
        req: RequestQueryBuilder.create({
            page: 1,
            limit: PER_PAGE,
        }),
    }),
    actions: {
        async fetchChatsList(requestQueryBuilder?: RequestQueryBuilder, setPage?: number): Promise<ChatsListChat[]> {
            if (requestQueryBuilder) {
                this.req = requestQueryBuilder;
            }

            this.req.setPage(setPage ?? this.chatsListPagination.page);
            this.req.setLimit(this.chatsListPagination.perPage);

            this.isLoadingChats = true;

            const {
                data: { data, count, total, page, pageCount },
            } = await axios.get(`${endpoint}/chats-list`, {
                params: this.req.queryObject,
            });

            this.chatsListPagination = {
                ...this.chatsListPagination,
                count,
                total,
                page,
                pageCount,
            };

            this.chatsList = data;
            this.isLoadingChats = false;

            return this.chatsList;
        },
        async loadMoreChatsListChats(pageNumber: number): Promise<void> {
            if (!this.hasMoreChatsListChats) {
                return;
            }

            this.chatsListPagination.page = pageNumber;

            if (pageNumber !== Number(router.currentRoute.query.page)) {
                router.replace({
                    name: "messaging-inbox",
                    params: {
                        ...router.currentRoute.params,
                    },
                    query: {
                        ...router.currentRoute.query,
                        page: pageNumber,
                    },
                });
            }

            this.chatsList = await this.fetchChatsList();

            this.isLoadingChats = false;
        },
        async fetchChat(chatId: number): Promise<ActiveChat> {
            this.isLoadingChat = true;
            this.chatLogPagination = {
                count: 0,
                pageCount: 0,
                page: 1,
                total: 0,
                perPage: PER_PAGE,
            };

            this.activeChat = {
                ...(await Promise.all([
                    axios.get<{ chat: ChatsListChat; contact: Contact }>(`${endpoint}/chats-list/${chatId}`),
                    this.fetchEntries(chatId),
                ]).then(([{ data: chat }, entries]) => ({
                    chat: chat.chat,
                    contact: chat.contact,
                    entries,
                }))),
            };

            this.isLoadingChat = false;

            return this.activeChat;
        },
        // Separate function, so its easier to lazy load.
        async fetchEntries(chatId: number): Promise<EntryDtoType[]> {
            const req = RequestQueryBuilder.create({
                page: this.chatLogPagination.page,
                limit: this.chatLogPagination.perPage,
            });

            // Note the data.data, because of pagination.
            const {
                data: { data: entries, count, total, page, pageCount },
            } = await axios.get(`${endpoint}/${chatId}/entries`, {
                params: req.queryObject,
            });

            this.chatLogPagination = {
                count,
                total,
                page,
                pageCount,
            };

            // @NOTE Bit hard to wrap your mind about this, but we (lazy) load in the other way around.
            // This means that the backend fetches the last x-items (x = limit items per request) by ORDER entries DESC (so latest first in array)
            // We need to do that because we read from bottom to top in an chat log, but per limit, we need to reverse that back otherwise
            // the last entry is parsed above the second-last entry.
            //return entries.reverse() as Array<EntryDtoType>;
            return entries as EntryDtoType[];
        },
        fetchEntry(entryId: number): Promise<EntryDtoType> {
            return axios.get<EntryDtoType>(`${endpoint}/entries/${entryId}`).then((response) => response.data);
        },
        async loadMoreEntries(): Promise<void> {
            if (!this.hasMoreEntries) {
                return;
            }

            this.isLoadingMoreEntries = true;
            this.chatLogPagination.page++;

            if (!this.activeChat || this.chatLogPagination.page >= this.chatLogPagination.total) {
                return;
            }

            const entries = (await this.fetchEntries(this.activeChat.chat.id)) as EntryDtoType[];
            this.activeChat?.entries.unshift(...entries);

            this.isLoadingMoreEntries = false;
        },
        addChat(chat: ChatsListChat): void {
            const { filterChannelsFn, filterTeamsFn, filterTagsFn, filterSearchFn } = useMessagingStatusFilters();

            // Only add the chat if we show it in the current filters
            if (filterChannelsFn(chat) && filterTeamsFn(chat) && filterTagsFn(chat) && filterSearchFn()) {
                this.chatsList.push(chat);
            }
        },
        async updateChat(chat: UpdateChatsListChat, shouldSwitchStatusFilter = false): Promise<void> {
            // First update active chat, then the chats list.
            if (!chat.id) {
                return;
            }

            // if the chat to be updated is the active chat, also update active chat.
            if (this.activeChat?.chat.id === chat.id) {
                if (shouldSwitchStatusFilter) {
                    const { switchStatusFilter } = useMessagingStatusFilters();
                    switchStatusFilter({
                        assignedUserId: chat.assignedUserId,
                        assignedTeamId: chat.assignedTeamId,
                        status: chat.status ?? this.activeChat.chat.status,
                    });
                }

                // ActiveChat chat (Chat type) differs a bit from chats list chat (ChatsListChat type)
                this.activeChat.chat = { ...this.activeChat.chat, ...chat };
            }

            //------------------------------------------------------------------
            // Show loader for corresponding chat in the chats list.
            this.isUpdatingChatId = chat.id;

            // There are several reasons why this can be -1 (exmaple over websockets etc).
            const chatsListChatIndex = this.chatsList.findIndex(
                (chatsListChat: ChatsListChat) => chatsListChat.id === chat.id
            );

            // @note that we ADD inside a update function.
            if (chatsListChatIndex === -1) {
                // Be sure to have the contact available to the chats list chat.
                if (!chat.contact && chat.contactId) {
                    const { getContactById, fetchContact } = useContactsStore();

                    // Try to get from store, otherwise fetch from backend.
                    let contact = getContactById(chat.contactId);
                    if (!contact) {
                        contact = await fetchContact(
                            RequestQueryBuilder.create({
                                filter: [
                                    {
                                        field: "id",
                                        operator: "$eq",
                                        value: chat.contactId,
                                    },
                                ],
                                fields: ["id", "name", "isHighPriority", "isBlocked"],
                            })
                        );
                    }

                    chat.contact = contact;
                }

                if (chat.contact) {
                    this.addChat(chat);
                }

                this.isUpdatingChatId = undefined;
                return;
            }

            // Replace to whole array entry, because reactive getters won't work deeply.
            this.chatsList.splice(chatsListChatIndex, 1, {
                ...this.chatsList[chatsListChatIndex],
                ...chat,
            }); // satisfies ChatsListChat

            this.isUpdatingChatId = undefined;
        },
        updateEntry(chatEntry: EntryDtoType): void {
            if (this.activeChat?.chat.id !== chatEntry.chatId) {
                return;
            }

            const entryIndex = this.activeChat?.entries.findIndex((entry) => entry.id === chatEntry.id);
            if (entryIndex === -1) {
                return;
            }

            // Replace to whole array entry, because reactive getters won't work deeply.
            this.activeChat?.entries.splice(entryIndex, 1, {
                ...this.activeChat?.entries[entryIndex],
                ...chatEntry,
            });
        },

        // @NOTICE only chats store related stuff!
        async sendMessage(chatId: number, message: SendMessage): Promise<void> {
            if (!this.activeChat || this.activeChat.chat.id !== message.chat?.id) {
                return;
            }

            // First add it as "draft"/"temporary" chat log entry.
            const tmpId = Number(uniqueId("9999999999"));

            const creationDate = DateTime.now() as unknown as Date;
            // This correspondents also with backend/websocket payloads, thats why some nulls are set.
            const chatEntry: AttachmentEntryDto | TextEntryDto = {
                chatId: this.activeChat.chat.id,
                components: message.content.components,
                createdAt: creationDate,
                createdByEntity: "user",
                createdById: message.channelActor.user?.id as number,
                direction: "out",
                externalReference: null,
                id: tmpId, // first random, until the backend request has been completed and we do have the real id
                message: message.content.text,
                metadata: message.metadata,
                status: "pending",
                statusReason: null,
                tags: null, // @todo?
                templateId: null,
                time: creationDate,
                type: message.content.attachments?.length ? "attachment" : "text",
                assets: message.content.attachments?.map((attachment) =>
                    AssetDto.fromAttachment(attachment as Attachment, {})
                ) as Asset[],
                assetIds: [],
            };

            // If the active chat is the same as the chat where we send the message to, add it to the active chat log.
            if (this.activeChat?.chat.id === chatId) {
                this.activeChat.entries.push(chatEntry);
            }

            //------------------------------------------------------------------

            const { data } = await axios.post<SendResponse>(`${process.env.VUE_APP_PUBLIC_API}/messages/send`, message);
            if (!data) {
                return;
            }

            // We can't do the below with the incoming "NEW_ENTRY" websocket event, because of the needed tmpId.
            const lastEntry = data.entries.at(-1);

            // Check again if we the message belongs to the same chat as the active chat. If so,
            // make the temporary definitive with the data from the backend, like id.
            if (this.activeChat?.chat.id === chatId) {
                const entryIndex = this.activeChat?.entries.findIndex((entry) => entry.id === tmpId);
                if (entryIndex === -1) {
                    return;
                }

                this.activeChat.entries.splice(entryIndex, 1, {
                    ...this.activeChat.entries[entryIndex],
                    id: lastEntry.id,
                    status: lastEntry.status ?? "none",
                    statusReason: lastEntry.statusReason,
                });
            }
        },
        closeChat(chatId?: number): void {
            if (!chatId || this.activeChat?.chat.id === chatId) {
                this.activeChat = null;

                router.replace({
                    name: "messaging-inbox",
                });
            }
        },
        deleteChat(chatId: number): void {
            this.isUpdatingChatId = chatId;

            const chatContactIndex = this.chatsList.findIndex((chat) => chat.id === chatId);
            if (chatContactIndex === -1) {
                return;
            }

            this.chatsList.splice(chatContactIndex, 1);

            this.isUpdatingChatId = undefined;
        },
        updateContactName({ contactId, name }) {
            this.chatsList.forEach((chatsListChat) => {
                if (chatsListChat.contact.id !== contactId) {
                    return;
                }
                if (chatsListChat.contact.name !== name) {
                    this.updateChat({
                        ...chatsListChat,
                        contact: {
                            ...chatsListChat.contact,
                            name,
                        },
                    });
                }
            });

            if (this.activeChat?.contact.id === contactId && this.activeChat.contact.name !== name) {
                this.activeChat.contact = {
                    ...this.activeChat?.contact,
                    name,
                };
            }
        },
        setAssignee(chatId: number, contactId: number, assignee?: Assignee): Assignee {
            if (!chatId || !contactId) {
                return;
            }

            this.updateChat(
                {
                    id: chatId,
                    contactId,
                    assignedUserId: assignee?.user?.id ?? null,
                    assignedTeamId: assignee?.team?.id ?? null,
                },
                true
            );

            return {
                user: {
                    id: assignee?.user?.id ?? null,
                },
                team: {
                    id: assignee?.team?.id ?? null,
                },
            };
        },
        setArchive(chatId: number, contactId: number, status: ChatStatus): void {
            if (!chatId || !contactId || !status) {
                return;
            }

            if (status === "closed") {
                this.closeChat();
            }

            this.updateChat(
                {
                    id: chatId,
                    contactId,
                    status,
                },
                true
            );
        },
        setReadStatus(chatId: number, read: boolean): void {
            if (!chatId || !read) {
                return;
            }

            const chatsListChat: UpdateChatsListChat = {
                id: chatId,
                read,
                ...(read ? { unreadMessages: 0 } : null),
            };

            this.updateChat(chatsListChat);
        },
    },
    getters: {
        getChatsListChatById(state): (chatId: number) => ChatsListChat | undefined {
            return (chatId: number) => state.chatsList.find((chatContact) => chatContact.id === chatId);
        },
        isActiveChatEnabled(state): boolean {
            const channelsStore = useChannelsStore();

            let mayChat;
            if (
                !this.activeChat?.chat.lastInboundMessageTime &&
                channelsStore.activeChatChannel?.customerCareWindowLength &&
                channelsStore.activeChatChannel?.customerCareWindowLength > 0
            ) {
                return false;
            }

            mayChat = resolveRemainingCustomerCareWindow(
                this.activeChat?.chat.lastInboundMessageTime,
                channelsStore.activeChatChannel?.customerCareWindowLength
            );

            // Should assign according to channel settings and it IS assigned, then youre allowed to send messages.
            if (
                shouldAssignV21(channelsStore.activeChatChannel) &&
                !isChatAssignedToMeV21(state.activeChat?.chat.assignedUserId)
            ) {
                mayChat = false;
            }

            return Boolean(mayChat);
        },
        hasMoreEntries(state): boolean {
            if (!state.activeChat) {
                return false;
            }

            return state.activeChat.entries.length < state.chatLogPagination.total;
        },
        hasMoreChatsListChats(state): boolean {
            return state.chatsList.length < state.chatsListPagination.total;
        },
    },
});
